'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import PricingCards from './PricingCards';
import FeatureTable from './FeatureTable';
import Testimonials from './Testimonials';
import FaqAccordion from './FaqAccordion';

export default function PricingPageLayout() {
  const [billing, setBilling] = useState<'monthly' | 'annual'>('monthly');

  return (
    <div className="min-h-screen bg-background">
      {/* Topbar */}
      <header className="h-14 border-b border-border bg-card/80 backdrop-blur-sm sticky top-0 z-30 flex items-center px-6 gap-4">
        <Link href="/" className="flex items-center gap-2">
          <AppLogo size={28} />
          <span className="font-bold text-base tracking-tight text-foreground">CompanyInbox</span>
        </Link>
        <div className="ml-auto flex items-center gap-3">
          <Link href="/inbox-dashboard" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
            Back to inbox
          </Link>
          <Link href="/sign-up-login-screen" className="btn-primary text-sm py-2 px-4">
            Sign in
          </Link>
        </div>
      </header>

      <main className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16 py-16">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="badge-permanent w-fit mx-auto mb-4">Upgrade your inbox</div>
          <h1 className="text-4xl xl:text-5xl font-bold text-foreground mb-4 leading-tight">
            Simple, transparent pricing
          </h1>
          <p className="text-lg text-secondary-foreground max-w-xl mx-auto mb-8">
            Start free. Upgrade when your address needs to last longer than a session.
          </p>

          {/* Billing toggle */}
          <div className="inline-flex items-center gap-1 p-1 bg-muted rounded-xl border border-border">
            <button
              onClick={() => setBilling('monthly')}
              className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                billing === 'monthly' ?'bg-card text-foreground shadow-card' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBilling('annual')}
              className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                billing === 'annual' ?'bg-card text-foreground shadow-card' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Annual
              <span className="text-xs font-bold text-accent bg-accent/10 border border-accent/25 px-1.5 py-0.5 rounded-md">
                Save 25%
              </span>
            </button>
          </div>
        </div>

        {/* Pricing cards */}
        <PricingCards billing={billing} />

        {/* Feature comparison */}
        <div className="mt-20">
          <h2 className="text-2xl font-bold text-foreground text-center mb-2">
            Everything in one place
          </h2>
          <p className="text-secondary-foreground text-center mb-10">
            Compare what each plan includes in full detail.
          </p>
          <FeatureTable />
        </div>

        {/* Testimonials */}
        <div className="mt-20">
          <h2 className="text-2xl font-bold text-foreground text-center mb-2">
            Trusted by developers and privacy-minded teams
          </h2>
          <p className="text-secondary-foreground text-center mb-10">
            From solo builders to small engineering teams.
          </p>
          <Testimonials />
        </div>

        {/* FAQ */}
        <div className="mt-20 max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-foreground text-center mb-2">
            Frequently asked questions
          </h2>
          <p className="text-secondary-foreground text-center mb-10">
            Everything you need to know before upgrading.
          </p>
          <FaqAccordion />
        </div>

        {/* Bottom CTA */}
        <div className="mt-20 text-center p-12 rounded-2xl shine-border bg-card relative overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-50" />
          <div className="relative z-10">
            <h2 className="text-3xl font-bold text-foreground mb-3">
              Ready to make it permanent?
            </h2>
            <p className="text-secondary-foreground mb-8 max-w-md mx-auto">
              Your address, your domain, no expiry. Start with Pro for less than a coffee a month.
            </p>
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <Link href="/sign-up-login-screen" className="btn-primary px-8 py-3 text-base">
                Start with Pro — $4/mo
              </Link>
              <Link href="/" className="btn-ghost px-8 py-3 text-base">
                Try free first
              </Link>
            </div>
          </div>
        </div>
      </main>

      <footer className="border-t border-border mt-16 py-8 px-6 text-center">
        <p className="text-sm text-muted-foreground">
          &copy; 2026 CompanyInbox. All rights reserved.{' '}
          <a href="#" className="text-primary hover:underline">Privacy</a>{' · '}
          <a href="#" className="text-primary hover:underline">Terms</a>
        </p>
      </footer>
    </div>
  );
}